import { createFileRoute, redirect, useNavigate } from "@tanstack/react-router";
import { useCallback, useState } from "react";
import ArrowForward from "~icons/material-symbols/arrow-forward";
import { css } from "../../../../styled-system/css";
import Header from "../../../components/header";
import { formatTimestamp } from "../../../libs/date";
import { AppError, ERROR_CODES } from "../../../libs/errors";
import { getSortedBalancesWithFundSources } from "../../../libs/get-sorted-balances-with-fund-sources";
import { t } from "../../../libs/i18n";
import { formatNumber } from "../../../libs/utils";

type Search = {
  from?: string;
};

export const Route = createFileRoute(
  "/_authed/transfer/input/$fundSourceId/$amount",
)({
  validateSearch: (search: Search) => {
    return search;
  },
  beforeLoad: ({ context: { tenant } }) => {
    if (!tenant.feature.transfer) {
      throw redirect({ to: "/" });
    }
  },
  loader: async ({
    context: { client },
    params: { fundSourceId, amount: amountParam },
  }) => {
    const balanceResponse = await client.getMyBalances({}).catch((error) => {
      throw new AppError(
        ERROR_CODES.CONNECT_GET_MY_BALANCES_FAILED,
        error.message,
        {
          originalError: error,
        },
      );
    });
    const { sortedBalances } = await getSortedBalancesWithFundSources(
      client,
      balanceResponse.balanceWithFundSources,
    );
    const targetBalance = sortedBalances.find(
      (b) => b.fundSource?.id === fundSourceId,
    );

    if (!targetBalance) {
      throw new Error("targetFundSource not found");
    }

    if (targetBalance.fundSource?.forbidTransfer) {
      throw new AppError(
        ERROR_CODES.CONNECT_GET_MY_BALANCES_FAILED,
        t("譲渡できないポイントが選択されました"),
        { skipSentry: true },
      );
    }

    const totalAmount = Number(targetBalance.userBalance?.amount || 0);

    return {
      amount: Number(amountParam),
      targetBalance,
      totalAmount,
    };
  },
  component: RouteComponent,
});

function RouteComponent() {
  const navigate = useNavigate();
  const { from } = Route.useSearch();
  const {
    targetBalance,
    totalAmount,
    amount: amountParam,
  } = Route.useLoaderData();
  const [amount, setAmount] = useState(amountParam);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [valid, setValid] = useState(amountParam > 0);

  const validate = useCallback(
    (n: number) => {
      let valid = true;
      let errorMessage = null;
      if (n > 100_000) {
        valid = false;
        errorMessage = t("一度に譲渡できるポイントの上限を超えています");
      }
      if (n > totalAmount) {
        valid = false;
        errorMessage = t("送るポイント額が残高を超えています");
      }
      if (n < 1) {
        valid = false;
        errorMessage = t("送るポイント額を入力してください");
      }
      return { valid, errorMessage };
    },
    [totalAmount],
  );

  const onChangeAmount = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = Number(e.target.value);
      const { valid, errorMessage } = validate(value);
      setValid(valid);
      setErrorMessage(errorMessage);

      if (Number.isNaN(value)) {
        setAmount(0);
      } else {
        setAmount(value);
      }
    },
    [validate],
  );

  const handleSubmit = useCallback(() => {
    const { valid, errorMessage } = validate(amount);
    if (!valid) {
      setValid(false);
      setErrorMessage(errorMessage);
      return;
    }
    navigate({
      to: "/transfer/qr/$fundSourceId/$amount",
      params: {
        fundSourceId: targetBalance.fundSource?.id || "", // 型安全のため。空になるケースはない。
        amount: amount.toString(),
      },
      search: { from },
    });
  }, [amount, navigate, targetBalance, validate, from]);

  return (
    <>
      <Header
        title={t("ポイントを送る")}
        onClickBack={() => {
          if (from === "balances") {
            navigate({ to: "/balances" });
          } else {
            navigate({ to: "/transfer" });
          }
        }}
      />
      <div
        className={css({
          padding: "16px",
          display: "flex",
          flexDir: "column",
          gap: "16px",
        })}
      >
        <div
          className={css({ display: "flex", flexDir: "column", gap: "8px" })}
        >
          <h1
            className={css({
              color: "text.primary",
            })}
          >
            {t("ポイント額入力")}
          </h1>
          <div
            className={css({
              color: "text.secondary",
              fontSize: "14px",
            })}
          >
            {t("送るポイントの額を入力してください")}
          </div>
        </div>

        <div
          className={css({
            bg: "white",
            borderRadius: "16px",
            p: "16px",
          })}
        >
          <div className={css({})}>
            <div
              className={css({
                display: "flex",
                flexDir: "column",
                gap: "8px",
                textAlign: "center",
              })}
            >
              <h3
                className={css({
                  fontWeight: "bold",
                  fontSize: "18px",
                })}
              >
                {targetBalance.fundSource?.name}
              </h3>

              {targetBalance.expirationDates.length > 0 && (
                <div
                  className={css({
                    fontSize: "14px",
                    color: "text.secondary",
                    bg: "surface.secondary",
                    p: "4px",
                    borderRadius: "14px",
                    border: "1px solid",
                    borderColor: "border.secondary",
                  })}
                >
                  {targetBalance.expirationDates.length > 1
                    ? t("最短{date}まで利用可能", {
                        date: formatTimestamp(targetBalance.expirationDates[0]),
                      })
                    : t("{date}まで利用可能", {
                        date: formatTimestamp(targetBalance.expirationDates[0]),
                      })}
                </div>
              )}
            </div>

            <div
              className={css({
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                borderBottom: "1px solid",
                borderColor: "border.secondary",
                mt: "24px",
              })}
            >
              <input
                value={amount}
                onChange={onChangeAmount}
                type="tel"
                // biome-ignore lint/a11y/noAutofocus: <explanation>
                autoFocus={true}
                placeholder={t("支払い額を入力")}
                className={css({
                  fontSize: "28px",
                  color: "text.primary",
                  textAlign: "right",
                  width: "100%",
                  px: "12px",
                })}
              />
              <span
                className={css({
                  fontSize: "16px",
                  color: "text.secondary",
                  fontWeight: "600",
                  pl: "16px",
                })}
              >
                pt
              </span>
            </div>
            {errorMessage && (
              <div
                className={css({
                  color: "text.accentError",
                  fontSize: "14px",
                  mt: "8px",
                  textAlign: "center",
                })}
              >
                {errorMessage}
              </div>
            )}
          </div>
          <div
            className={css({
              fontSize: "14px",
              color: "text.secondary",
              mt: "8px",
              textAlign: "center",
            })}
          >
            {t("ポイント残高 {amount}pt", {
              amount: formatNumber(totalAmount),
            })}
          </div>
        </div>
        <button
          onClick={handleSubmit}
          disabled={!valid}
          type="button"
          className={css({
            width: "100%",
            padding: "12px",
            backgroundColor: valid ? "surface.accentPrimary" : "#e0e0e0",
            color: valid ? "white" : "#666",
            borderRadius: "16px",
            fontWeight: "bold",
            cursor: valid ? "pointer" : "not-allowed",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "8px",
          })}
        >
          <div>{t("次へ")}</div>
          <ArrowForward
            className={css({
              width: "24px",
              height: "24px",
            })}
          />
        </button>
      </div>
    </>
  );
}
